import type { TextComposerFramePreviewOutput } from "./frameOutput";
import {
  isTextTransportMedium,
  type TextTransportMedium,
} from "./framePersistence";
import type { TextComposerAuthoringState } from "../editor/authoringState";
import type { TextComposerExtension } from "../editor/extensionContract";
import {
  collectFrameVariantConfigKeys,
  composeFrameVariantConfig,
} from "../editor/extensionRegistry";
import { defaultExtensions } from "../extensions/defaultExtensions";
import type { ProductGridModelValue } from "@/features/EmailEditor/plugins/productGridConstants";
import type {
  CreateFrameVariantPayloadParsed,
  FrameVariantConfigParsed,
  UpdateFramePayloadParsed,
  UpdateFrameVariantParsed,
} from "@/frames/schemas";
import type { Frame, FrameVariant } from "@/types/dragon-tiles";

import {
  buildFrameVariantConfig,
  frameReplaceFields,
  narrowVariantToUpdate,
  updateFramePayload,
} from "@/frames/service";

const FALLBACK_MEDIUMS: ReadonlySet<TextTransportMedium> = new Set([
  "sms",
  "mms",
]);

function isFallbackMedium(
  medium: FrameVariant["medium"],
): medium is "sms" | "mms" {
  return FALLBACK_MEDIUMS.has(medium as TextTransportMedium);
}

function mergeFrameVariantConfig(
  existing: FrameVariant["config"],
  contributed: Partial<FrameVariantConfigParsed>,
  ownedKeys: Set<string>,
): FrameVariantConfigParsed {
  const cleaned: Record<string, unknown> = { ...(existing ?? {}) };
  for (const key of ownedKeys) {
    delete cleaned[key];
  }
  return buildFrameVariantConfig({
    existing: { ...cleaned, ...contributed },
  });
}

interface BuildContributedVariantConfigArgs {
  authoringState: TextComposerAuthoringState;
  productModel?: ProductGridModelValue;
  extensions?: TextComposerExtension[];
}

/** Extension contributions merged and validated through centralized frame config helpers. */
export function buildTextComposerContributedVariantConfig(
  args: BuildContributedVariantConfigArgs,
): FrameVariantConfigParsed {
  const extensions = args.extensions ?? defaultExtensions;
  const contributedConfig = composeFrameVariantConfig(extensions)(
    args.authoringState,
    { productModel: args.productModel },
  );
  return buildFrameVariantConfig({
    existing: contributedConfig,
  });
}

interface BuildWireRowsArgs {
  frameOutput: TextComposerFramePreviewOutput;
  productModel: ProductGridModelValue | undefined;
  extensions?: TextComposerExtension[];
}

type TextComposerWireVariant = Extract<
  CreateFrameVariantPayloadParsed,
  { medium: "sms" | "mms" | "rcs" }
>;

function variantAuthoringState(variant: {
  content_json?: { authoringState?: unknown } | null;
}): TextComposerAuthoringState {
  return (variant.content_json?.authoringState ??
    {}) as TextComposerAuthoringState;
}

/** Wire rows for create-frame or update-frame payloads (SMS/MMS/RCS text composer variants). */
export function buildTextComposerFrameVariantWireRows(
  args: BuildWireRowsArgs,
): TextComposerWireVariant[] {
  const extensions = args.extensions ?? defaultExtensions;

  return args.frameOutput.frame.frame_variants
    .filter(
      (
        variant,
      ): variant is (typeof args.frameOutput.frame.frame_variants)[number] & {
        medium: "sms" | "mms" | "rcs";
      } => isTextTransportMedium(variant.medium),
    )
    .map((variant) => ({
      medium: variant.medium,
      content_text: "",
      content_url: "",
      content_json: variant.content_json,
      config: buildTextComposerContributedVariantConfig({
        authoringState: variantAuthoringState(variant),
        productModel: args.productModel,
        extensions,
      }),
    }));
}

export interface TextComposerFrameUpdateResult {
  payload: UpdateFramePayloadParsed;
}

/**
 * Merges Text Composer preview output into an existing frame: updates or adds
 * `sms` / `rcs` / `mms` variants and leaves other mediums (e.g. `email`)
 * unchanged.
 *
 * The PATCH endpoint full-reconciles `frame_variants` by `medium`: any existing
 * variant whose medium is omitted from the payload is soft-deleted server-side.
 * So the returned payload carries the complete desired variant set (including
 * preserved non-text mediums) and dropped text transports are simply omitted.
 */
export function buildUpdateTextComposerFramePayload(args: {
  frame: Frame;
  frameOutput: TextComposerFramePreviewOutput;
  productModel?: ProductGridModelValue;
  extensions?: TextComposerExtension[];
  name?: string;
}): TextComposerFrameUpdateResult {
  const extensions = args.extensions ?? defaultExtensions;
  const contributeConfig = composeFrameVariantConfig(extensions);
  const ownedKeys = collectFrameVariantConfigKeys(extensions);

  const wireRows = buildTextComposerFrameVariantWireRows({
    frameOutput: args.frameOutput,
    productModel: args.productModel,
    extensions,
  });

  const preservedNonText: UpdateFrameVariantParsed[] = args.frame.frame_variants
    .filter((v) => !isTextTransportMedium(v.medium))
    .map(narrowVariantToUpdate);

  const mergedTextParts = wireRows.map((row) => {
    const outputVariant = args.frameOutput.frame.frame_variants.find(
      (v) => v.medium === row.medium,
    );
    const exactMatch = args.frame.frame_variants.find(
      (v) => v.medium === row.medium,
    );
    const existing =
      exactMatch ??
      (isFallbackMedium(row.medium)
        ? args.frame.frame_variants.find(
            (v) => isFallbackMedium(v.medium) && v.medium !== row.medium,
          )
        : undefined);
    if (existing !== undefined) {
      const contributedConfig = contributeConfig(
        variantAuthoringState(outputVariant ?? { content_json: null }),
        { productModel: args.productModel },
      );
      return narrowVariantToUpdate({
        ...existing,
        medium: row.medium,
        content_text: "",
        content_url: "",
        content_json: outputVariant?.content_json ?? existing.content_json,
        config: mergeFrameVariantConfig(
          existing.config,
          contributedConfig,
          ownedKeys,
        ),
      });
    }
    return {
      ...row,
      content_text: "",
      content_url: "",
      content_json: outputVariant?.content_json ?? null,
    };
  });

  const frame_variants = [...preservedNonText, ...mergedTextParts];

  return {
    payload: updateFramePayload({
      ...frameReplaceFields(args.frame),
      ...(args.name !== undefined ? { name: args.name } : {}),
      frame_variants,
    }),
  };
}
